<script setup lang="ts">
import { useScroll } from '@vueuse/core'
import { computed, ref, type StyleValue } from 'vue'

type Props = {
  /**
   * Size of the fade gradients
   */
  fadeSize?: 'md' | 'lg' | 'xl'
  /**
   * Distance in pixels over which the fade effect transitions
   */
  fadeDistance?: number
}

const props = withDefaults(defineProps<Props>(), {
  fadeSize: 'md',
  fadeDistance: 40,
})

const scrollContainer = ref<HTMLElement>()
const { y: scrollY } = useScroll(scrollContainer)

const FADE_SIZE_VALUES = {
  md: 40,
  lg: 80,
  xl: 160,
} as const

const fadeSizeValue = computed(() => FADE_SIZE_VALUES[props.fadeSize])

/** Calculate when to show fade indicators */
const topFade = computed(() => {
  const scrolled = scrollY.value
  return Math.min(scrolled / props.fadeDistance, 1)
})

const bottomFade = computed(() => {
  if (!scrollContainer.value) return 0

  const { scrollHeight, clientHeight } = scrollContainer.value
  const maxScroll = scrollHeight - clientHeight
  const scrolledFromBottom = maxScroll - scrollY.value

  return Math.min(scrolledFromBottom / props.fadeDistance, 1)
})

const maskStyle = computed<StyleValue>(() => {
  const fadeSize = fadeSizeValue.value
  const top = topFade.value
  const bottom = bottomFade.value

  const topStop = top > 0 ? `transparent 0px, black ${fadeSize * top}px` : `black 0px`
  const bottomStop =
    bottom > 0 ? `black calc(100% - ${fadeSize * bottom}px), transparent 100%` : `black 100%`

  // Create mask that fades only the edges when scrollable
  return {
    mask: `linear-gradient(to bottom, ${topStop}, black, ${bottomStop})`,
  }
})
</script>

<template>
  <!-- Scroll container with edge fade effects -->
  <div
    ref="scrollContainer"
    class="go-scrollbar shrink grow overflow-auto"
    :style="maskStyle"
  >
    <slot />
  </div>
</template>
